import { deepMerge } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { VisibilityState } from '@tanstack/table-core';
import {
  MRT_Row,
  MRT_TableOptions,
  MRT_RowSelectionState,
} from 'mantine-react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import { ResultsDataColumnControl } from '../results-data-column-control/results-data-column-control';
import { intercultureApi } from '../../data-access/apis/interculture';
import type { IntercultureForm } from '../../data-access/types/types';
import { useIntercultureForms } from '../../hooks/use-interculture-forms/interculture-forms';
import { ColumnSegmentedControl } from '../column-segmented-control/column-segmented-control';
import { ForceResultModal } from '../force-result-modal/force-result-modal';

import { sowingColState, harvestColState, useTable } from './use-table';

import { FORM_STATUS, useFiltersTableOptions } from '@data-access';
import { PfTable, useRowSelection } from '@ui';

export interface IntercultureAdviceTableProps {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: any;
  selectedType: string;
  onEdit: (id: number) => void;
}

export function IntercultureAdviceTable({
  rowSelection,
  setRowSelection,
  selectedType,
  onEdit,
}: IntercultureAdviceTableProps) {
  const { t } = useTranslation();
  const [currentResult, setCurrentResult] = useState<
    IntercultureForm | undefined
  >();
  const [opened, { open, close }] = useDisclosure(false);

  const filtersTableOptions =
    useFiltersTableOptions<IntercultureForm>('interculture');

  const intercultureForms = useIntercultureForms({
    type: selectedType,
  });
  const forceResult = useMutation({
    mutationFn: intercultureApi.createForcedResult,
  });

  const handleOpenForceResultModal = (result: IntercultureForm) => {
    setCurrentResult(result);
    open();
  };

  const { columns } = useTable({
    type: selectedType,
    onOpenForceResult: handleOpenForceResultModal,
  });

  const rowSelectionProps = useRowSelection<IntercultureForm>({
    data: intercultureForms.data?.results || [],
    rowSelection,
    setRowSelection,
  });

  const tableOptions: MRT_TableOptions<IntercultureForm> = deepMerge<
    MRT_TableOptions<IntercultureForm>
  >(
    {
      data: intercultureForms.data?.results || [],
      columns,
      rowCount: intercultureForms.data?.count || 0,

      initialState: {
        columnVisibility: harvestColState as VisibilityState,
        showColumnFilters: true,
      },
      state: {
        isLoading: intercultureForms.isLoading,
        rowSelection,
        showProgressBars: intercultureForms.isRefetching,
      },
      onRowSelectionChange: setRowSelection,

      mantinePaperProps: {
        h: '100%',
      },

      ...rowSelectionProps,
      renderTopToolbarCustomActions: ({ table }) => (
        <ColumnSegmentedControl table={table as any} />
      ),
    },
    filtersTableOptions,
  );

  const handleEdit = (row: MRT_Row<IntercultureForm>) => {
    onEdit(row.original.id);
  };

  const handleSubmitResult = (values: {
    status: FORM_STATUS;
    comment: string;
  }) => {
    if (currentResult) {
      forceResult.mutate({
        form: currentResult.id,
        comment: values.comment,
        forced_status: values.status,
      });
    }
    setCurrentResult(undefined);
    close();
  };

  const defaultValidationStatus = currentResult?.forced_result
  ? currentResult.forced_result.estimated_status
  : currentResult?.status || FORM_STATUS.REJECTED;

  return (
    <>
      <PfTable
        enableShowHideColumns={false}
        onEditActionClick={handleEdit}
        options={tableOptions}
      />
      <ForceResultModal
        close={close}
        defaultValidationStatus={defaultValidationStatus}
        forcedResult={currentResult?.forced_result}
        onSubmit={handleSubmitResult}
        opened={opened}
      />
    </>
  );
}

export default IntercultureAdviceTable;
